import { AuthApiError } from '@/lib/auth/authApi'
import { getSessionToken } from '@/lib/auth/authStorage'
import { rememberOAuthReturn } from '@/lib/auth/oauthReturn'

/** The signed query the provider sent this page to, without the leading "?". */
export const signedOAuthQuery = (): string => window.location.search.replace(/^\?/, '')

/** Off to sign in; resolvePostAuth brings the user back to this page afterwards. */
export function signInThenReturn(): void {
  rememberOAuthReturn()
  window.location.href = '/auth'
}

/** The session token, or null after sending the user to sign in. */
export function sessionOrSignIn(): string | null {
  const token = getSessionToken()
  if (!token) signInThenReturn()
  return token
}

export type OAuthFlowError = 'invalidRequest' | 'failed'

/**
 * Runs a provider call: a stale session goes back through sign-in, a bad or expired
 * signed query is `invalidRequest`, anything else `failed`.
 */
export async function runOAuthStep(step: () => Promise<string>): Promise<{ url: string } | { error: OAuthFlowError } | null> {
  try {
    return { url: await step() }
  } catch (error) {
    if (error instanceof AuthApiError && error.status === 401) {
      signInThenReturn()
      return null
    }
    return { error: error instanceof AuthApiError && error.status === 400 ? 'invalidRequest' : 'failed' }
  }
}

export type ScopeKey = 'read' | 'write' | 'admin' | 'identity' | 'offline'

/** The requested scopes as the lines we show: `<root>/mcp/<x>`, OIDC identity, offline. */
export function scopeLines(scope: string | null): Array<ScopeKey> {
  const lines = new Set<ScopeKey>()
  for (const value of (scope ?? '').split(' ').filter(Boolean)) {
    if (value.endsWith('/mcp/read')) lines.add('read')
    else if (value.endsWith('/mcp/write')) lines.add('write')
    else if (value.endsWith('/mcp/admin')) lines.add('admin')
    else if (value === 'offline_access') lines.add('offline')
    else if (value === 'openid' || value === 'profile' || value === 'email') lines.add('identity')
  }
  const order: Array<ScopeKey> = ['read', 'write', 'admin', 'identity', 'offline']
  return order.filter((key) => lines.has(key))
}
